import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import { parseBoolean, parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export enum AssetHealthActionCostType {
  CAPEX = 'CAPEX',
  OPEX = 'OPEX',
}

export enum OpexSubtype {
  MAINTENANCE = 'Maintenance',
  MISCELLANEOUS = 'Miscellaneous',
}

export enum CapexSubtype {
  INFRASTRUCTURE = 'Infrastructure',
  MISCELLANEOUS = 'Miscellaneous',
}

export type CostSubtype = OpexSubtype | CapexSubtype

export enum AssetHealthActionStatus {
  PENDING = 'Pending Approval',
  COMPLETED = 'Completed',
  APPROVED = 'Approved',
}

/**
 * Represents an Asset Health Action entry (component health plan action)
 */
export class GeckoAssetHealthAction extends GeckoEntryBase<
  {
    name: string
    actionDate: string
    actionStatus: AssetHealthActionStatus
    actionType?: string
    actionCost?: number
    created_time: number
    implemented: boolean
    costTypeHierarchy?: string[]
    area?: number
    costTotal?: number | null
    minRl?: number
    remainingLifeAdded?: number
    componentId?: string
    componentName?: string
    componentType?: string
    linkedEntryId?: string
    selectedAnnotationIds?: string[]
    fixedAreaFt2?: number
    isFullReplacement?: boolean
    budgetBreakdowns?: Record<string, number>
    actionTags?: string[]
    deficiencyIds?: string[]
    riskScenarioIds?: string[]
  },
  {
    organization: string
    site: string
    unit?: string
    component?: string
    [RolodexEntryType.COMPONENT_HEALTH_PLAN]: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        name: parseString(json.data.name) ?? 'Unnamed Action',
        actionDate: parseString(json.data.actionDate) ?? '',
        actionStatus:
          (json.data.actionStatus as AssetHealthActionStatus) ??
          AssetHealthActionStatus.PENDING,
        actionType: parseString(json.data.actionType),
        actionCost: parseNumber(json.data.actionCost),
        created_time: parseNumber(json.data.created_time) ?? Date.now(),
        implemented: parseBoolean(json.data.implemented) ?? false,
        costTypeHierarchy: Array.isArray(json.data.costTypeHierarchy)
          ? (json.data.costTypeHierarchy as string[])
          : undefined,
        area: parseNumber(json.data.area),
        costTotal: parseNumber(json.data.costTotal),
        minRl: parseNumber(json.data.minRl),
        remainingLifeAdded: parseNumber(json.data.remainingLifeAdded),
        componentId: parseString(json.data.componentId),
        componentName: parseString(json.data.componentName),
        componentType: parseString(json.data.componentType),
        linkedEntryId: parseString(json.data.linkedEntryId),
        selectedAnnotationIds: Array.isArray(json.data.selectedAnnotationIds)
          ? json.data.selectedAnnotationIds.filter(
              (id): id is string => typeof id === 'string'
            )
          : undefined,
        fixedAreaFt2: parseNumber(json.data.fixedAreaFt2),
        isFullReplacement: parseBoolean(json.data.isFullReplacement),
        budgetBreakdowns: json.data.budgetBreakdowns as
          | Record<string, number>
          | undefined,
        actionTags: Array.isArray(json.data.actionTags)
          ? (json.data.actionTags as string[])
          : undefined,
        deficiencyIds: Array.isArray(json.data.deficiencyIds)
          ? json.data.deficiencyIds.filter(
              (id): id is string => typeof id === 'string'
            )
          : undefined,
        riskScenarioIds: Array.isArray(json.data.riskScenarioIds)
          ? json.data.riskScenarioIds.filter(
              (id): id is string => typeof id === 'string'
            )
          : undefined,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit),
        component: parseString(json.tags.component),
        [RolodexEntryType.COMPONENT_HEALTH_PLAN]:
          parseString(json.tags[RolodexEntryType.COMPONENT_HEALTH_PLAN]) ?? '',
      },

      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoAssetHealthAction(json)
  }
}
